/**
 * The hardware a job is putting in: what is still to scan, and what is in.
 *
 * Acceptance pre-registers a shell for every camera the quote sold. This card
 * is where those shells become real — one serial at a time, usually from a
 * ladder, usually with no signal. So it reads from the device and writes
 * through `actions.commission`, which queues when the server cannot be
 * reached (D47).
 *
 * One asset is "open" at a time. The serial field takes focus when it opens,
 * because a hand-held barcode scanner is a keyboard that types the label and
 * presses Enter: the tech points, pulls the trigger, and the row is done.
 * Nothing about that needs a camera or a permission prompt.
 */
import { useEffect, useRef, useState } from "react";

import * as actions from "../lib/actions";
import { ApiError } from "../lib/api";
import { day } from "../lib/format";
import { useLocal } from "../lib/local";
import * as repo from "../lib/repo";
import type { InstallState } from "../lib/repo";
import type { Row } from "../lib/store";
import { Card, Chip } from "./ui";

type Said = { tone: "ok" | "info" | "crit"; text: string };

export function InstallRegister({ jobId }: { jobId: string }) {
  const { data, reload } = useLocal(() => repo.installRegister(jobId), [jobId]);
  const [open, setOpen] = useState<string | null>(null);
  const [said, setSaid] = useState<Said | null>(null);

  // Nothing sold on this job creates an asset — a service call, a labour-only
  // job. Say nothing rather than showing an empty register.
  if (!data || data.awaiting.length + data.installed.length === 0) return null;

  const total = data.awaiting.length + data.installed.length;
  const first = data.awaiting[0]?.id ?? null;

  return (
    <Card
      title="Install register"
      subtitle={
        data.awaiting.length === 0
          ? `All ${total} in`
          : `${data.installed.length} of ${total} in · ${data.awaiting.length} to scan`
      }
      padded={false}
    >
      {said && (
        <p
          className={`notice notice-${said.tone}`}
          role={said.tone === "crit" ? "alert" : "status"}
          style={{ margin: 12 }}
        >
          {said.text}
        </p>
      )}

      <ul className="register">
        {data.awaiting.map((asset) => (
          <li key={asset.id} className="register-row">
            <AwaitingRow
              asset={asset}
              jobId={jobId}
              isOpen={(open ?? first) === asset.id}
              onOpen={() => {
                setOpen(asset.id);
                setSaid(null);
              }}
              onDone={(result) => {
                setSaid(result);
                setOpen(null); // falls through to the next unscanned one
                reload();
              }}
              onRefused={(text) => setSaid({ tone: "crit", text })}
            />
          </li>
        ))}
        {data.installed.map((asset) => (
          <li key={asset.id} className="register-row register-done">
            <div className="register-head">
              <b className="mono">{String(asset.ref)}</b>
              <span className="mono tiny">SN {String(asset.serial)}</span>
              <InstallChip state={asset.install?.state} />
            </div>
            <div className="tiny muted">
              {[asset.location_desc, installedLine(asset)].filter(Boolean).join(" · ")}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function InstallChip({ state }: { state: InstallState["state"] | undefined }) {
  if (state === "pending") return <Chip tone="warn">waiting to send</Chip>;
  if (state === "sent") return <Chip tone="info">sent</Chip>;
  return <Chip tone="ok">in</Chip>;
}

function installedLine(asset: Row & { install?: InstallState }): string {
  if (asset.install?.state === "pending") return "warranty worked out when it sends";
  if (asset.install?.state === "sent") return "warranty date arrives with the next refresh";
  const parts: string[] = [];
  if (asset.installed_on) parts.push(`installed ${day(String(asset.installed_on))}`);
  if (asset.warranty_expires_on) parts.push(`warranty to ${day(String(asset.warranty_expires_on))}`);
  return parts.join(", ");
}

function AwaitingRow({
  asset,
  jobId,
  isOpen,
  onOpen,
  onDone,
  onRefused,
}: {
  asset: Row & { refused?: string };
  jobId: string;
  isOpen: boolean;
  onOpen: () => void;
  onDone: (said: Said) => void;
  onRefused: (text: string) => void;
}) {
  const [serial, setSerial] = useState("");
  const [where, setWhere] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) input.current?.focus();
  }, [isOpen]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!serial.trim() || busy) return;
    setBusy(true);
    try {
      const { result, queued, message } = await actions.commission(String(asset.id), serial, {
        job_id: jobId,
        location_desc: where.trim() || undefined,
      });
      onDone({
        tone: queued ? "info" : "ok",
        text: result?.said ?? message ?? `${String(asset.ref)} registered.`,
      });
    } catch (err) {
      onRefused(err instanceof ApiError ? err.message : "Couldn't register that serial.");
      input.current?.select();
    } finally {
      setBusy(false);
    }
  }

  const label = [asset.manufacturer, asset.model].filter(Boolean).join(" ");

  // A scan the server turned down, shown against the asset it was meant for
  // until a corrected scan replaces it. Never swallowed (D15).
  const refused = asset.refused ? (
    <p className="tiny text-crit" role="alert" style={{ margin: "4px 0 0" }}>
      Not registered — {asset.refused}
    </p>
  ) : null;

  if (!isOpen) {
    return (
      <>
        <button type="button" className="register-head register-closed" onClick={onOpen}>
          <b className="mono">{String(asset.ref)}</b>
          <span className="tiny muted">{label}</span>
          <Chip tone={asset.refused ? "crit" : "neutral"}>{asset.refused ? "refused" : "to scan"}</Chip>
        </button>
        {refused}
      </>
    );
  }

  return (
    <form className="register-open" onSubmit={(e) => void submit(e)}>
      <div className="register-head">
        <b className="mono">{String(asset.ref)}</b>
        <span className="tiny muted">{label}</span>
      </div>
      {refused}
      <label className="field">
        <span>Serial</span>
        <input
          ref={input}
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
          placeholder="Scan the label, or type it"
          autoComplete="off"
          autoCapitalize="none" /* case is kept as the label reads it */
          spellCheck={false}
          inputMode="text"
          enterKeyHint="done"
          className="mono"
        />
      </label>
      <label className="field">
        <span>Where it went (optional)</span>
        <input
          value={where}
          onChange={(e) => setWhere(e.target.value)}
          placeholder="Loading dock, east wall"
          autoComplete="off"
        />
      </label>
      <div className="btn-row">
        <button className="btn btn-primary" type="submit" disabled={busy || !serial.trim()}>
          {busy ? "Registering…" : "Register"}
        </button>
      </div>
    </form>
  );
}
